import { useState } from "react";

/**
 * "Reload once to the new version."
 *
 * Every route is a lazy chunk with a hashed name, and a deploy removes the old
 * chunks. A tab opened before the deploy then asks for a file that is gone,
 * and the route shows its error screen until someone reloads. A reload fetches
 * the new index.html, which names the new chunks — so we do it for them, ONCE:
 * the time of the last automatic reload is kept in sessionStorage and a second
 * one within `STALE_CHUNK_RELOAD_GUARD_MS` is refused, so a file that is
 * genuinely missing shows the error screen instead of reloading forever.
 */

export const STALE_CHUNK_RELOAD_KEY = "seagames.chunk-reload-at";
export const STALE_CHUNK_RELOAD_GUARD_MS = 30_000;

/**
 * What the browsers (and Vite's preloader) say when a module script or a
 * chunk's CSS could not be fetched. Anchored on those exact phrases so an
 * ordinary API error or a TypeError from app code never matches.
 */
const STALE_CHUNK_MESSAGES = [
	/failed to fetch dynamically imported module/i, // Chrome, Edge
	/error loading dynamically imported module/i, // Firefox
	/importing a module script failed/i, // Safari
	/unable to preload css/i, // Vite's CSS preload
	/^failed to load module script/i, // Chrome: wrong MIME (an HTML 404 page served for a .js)
];

export function isStaleChunkError(err: unknown): boolean {
	if (!err || typeof err !== "object") return false;
	const message = (err as { message?: unknown }).message;
	if (typeof message !== "string") return false;
	return STALE_CHUNK_MESSAGES.some((re) => re.test(message));
}

/** Set once this page has asked for a reload; the next page starts without it. */
let reloadPending = false;

/** Was this page itself loaded by a reload? Only consulted when storage is unusable. */
function pageWasReloaded(): boolean {
	try {
		const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
		return nav?.type === "reload";
	} catch {
		return false;
	}
}

/**
 * Reloads the page unless an automatic reload already happened within the
 * guard window. Returns true when a reload is under way (including one this
 * page already started), false when it refused — show the error then.
 */
export function reloadForStaleChunk(): boolean {
	if (reloadPending) return true;
	const now = Date.now();
	let storageOk = true;
	try {
		const last = Number(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY));
		if (Number.isFinite(last) && last > 0 && now - last < STALE_CHUNK_RELOAD_GUARD_MS) return false;
		window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
	} catch {
		storageOk = false;
	}
	// No storage to remember the last reload: a page that is itself the result
	// of a reload does not reload again, so a missing file cannot loop.
	if (!storageOk && pageWasReloaded()) return false;
	reloadPending = true;
	window.location.reload();
	return true;
}

let installed = false;

/**
 * Vite fires `vite:preloadError` when a lazy chunk (or its CSS) fails to load;
 * `preventDefault()` stops it rethrowing. Call once, before the first render.
 */
export function installStaleChunkReload(): void {
	if (installed || typeof window === "undefined") return;
	installed = true;
	window.addEventListener("vite:preloadError", (event) => {
		const payload = (event as Event & { payload?: unknown }).payload;
		if (isStaleChunkError(payload) && reloadForStaleChunk()) event.preventDefault();
	});
}

/**
 * For a route's errorComponent: true when the screen should render nothing
 * because the page is reloading to the new version (decided once per mount,
 * so it never flashes the error first). False → show the error screen.
 */
export function useStaleChunkReload(error: unknown): boolean {
	const [reloading] = useState(() => reloadPending || (isStaleChunkError(error) && reloadForStaleChunk()));
	return reloading || reloadPending;
}

/** Tests only: forget this page's reload. */
export function __resetStaleChunkForTests(): void {
	reloadPending = false;
	installed = false;
}
